import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Filter, Search } from "lucide-react";
import type {
  Lead,
  WorkspaceList,
  DirectoryPerson,
  SavedPersonSearch,
} from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import {
  bulkDeleteLeads,
  deleteLead,
  exportLeadsCsv,
} from "@/api/leads";
import {
  createSavedSearch,
  enrichPeople,
  exportPeopleCsv,
  listSavedSearches,
  savePeople,
  searchPeople,
} from "@/api/people";
import { downloadBlob } from "@/api/download";
import {
  createWorkspaceList,
  listWorkspaceLists,
} from "@/api/lists";
import { addDiscoveredOffers } from "@/api/discovery";
import {
  EMPTY_PEOPLE_FILTERS,
  PeopleFilters,
  apiFiltersToState,
  filtersToApi,
  seedFiltersFromIcp,
  type PeopleFilterState,
} from "@/components/people/people-filters";
import {
  buildFilterChips,
  PeopleFilterChips,
} from "@/components/people/people-filter-chips";
import {
  decodeFiltersParam,
  encodeFiltersParam,
} from "@/components/people/people-filter-config";
import { PeopleResearchAiModal } from "@/components/people/people-research-ai-modal";
import {
  PeopleResultsTable,
  personRowId,
} from "@/components/people/people-results-table";
import { PersonDrawer } from "@/components/people/person-drawer";
import { SavedPersonDrawer } from "@/components/people/saved-person-drawer";
import { MobileSheet } from "@/components/mobile-sheet";
import { WorkflowEnrollPicker } from "@/components/workflows/enroll-picker";
import { listIcps } from "@/api/icp";
import { leadToExploreResult, parseLeadEnvelopes } from "@/lib/lead-envelope";

type Mode = "total" | "net-new";

export type PeopleDirectoryScope = "directory" | "lead";

export function PeopleDirectoryPage({
  scope,
}: {
  scope: PeopleDirectoryScope;
}) {
  const isLead = scope === "lead";
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<PeopleFilterState>(() => {
    const encoded = searchParams.get("filters");
    if (encoded) {
      const decoded = decodeFiltersParam(encoded);
      if (decoded && typeof decoded === "object") {
        return { ...EMPTY_PEOPLE_FILTERS, ...(decoded as PeopleFilterState) };
      }
    }
    return EMPTY_PEOPLE_FILTERS;
  });
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [topSearch, setTopSearch] = useState(
    () => searchParams.get("q") ?? "",
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [mode, setMode] = useState<Mode>("total");
  const [people, setPeople] = useState<DirectoryPerson[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [drawer, setDrawer] = useState<DirectoryPerson | null>(null);
  const [leadDrawer, setLeadDrawer] = useState<Lead | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [listPicker, setListPicker] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [saveSearchOpen, setSaveSearchOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState("");
  const [searching, setSearching] = useState(false);
  const [enrichingId, setEnrichingId] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(true);
  const [researchOpen, setResearchOpen] = useState(false);
  const autoRan = useRef(false);
  const lastUrlQ = useRef<string | null>(null);

  const { data: listsData } = useQuery({
    queryKey: ["workspace-lists", "people"],
    queryFn: () => listWorkspaceLists({ modality: "people" }),
    staleTime: 30_000,
  });
  const lists = listsData?.lists ?? [];

  const { data: savedData } = useQuery({
    queryKey: ["saved-person-searches"],
    queryFn: listSavedSearches,
    staleTime: 30_000,
  });
  const savedSearches = savedData?.searches ?? [];

  const { data: icpData } = useQuery({
    queryKey: ["icp"],
    queryFn: listIcps,
    staleTime: 60_000,
  });
  const icps = icpData?.icps ?? [];

  const saveMutation = useMutation({
    mutationFn: (args: {
      people: DirectoryPerson[];
      listId?: string;
      createDeal?: boolean;
    }) =>
      savePeople({
        people: args.people,
        listId: args.listId,
        createDeal: args.createDeal,
        saveLeads: true,
        ensureCompanies: true,
      }),
    onSuccess: (res, vars) => {
      setMessage(`Added ${res.saved} to leads, skipped ${res.skipped}`);
      setError(null);
      const leadIdByPersonId = new Map(
        res.leads
          .filter((lead) => lead.personId)
          .map((lead) => [lead.personId!, lead.leadId]),
      );
      const selectedPersonKeys = new Set(
        (vars.people ?? []).flatMap((p) =>
          [p.personId, p.apolloPersonId].filter(Boolean),
        ),
      );
      setPeople((prev) =>
        prev.map((p) => {
          const key = p.personId || p.apolloPersonId;
          if (!key || !selectedPersonKeys.has(key)) return p;
          const leadId =
            leadIdByPersonId.get(p.personId ?? "") ??
            leadIdByPersonId.get(p.apolloPersonId ?? "") ??
            res.people.find(
              (saved) =>
                saved.personId === p.personId ||
                saved.apolloPersonId === p.apolloPersonId,
            )?.leadId ??
            p.leadId;
          return {
            ...p,
            alreadySaved: true,
            leadId,
          };
        }),
      );
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: ["workspace-lists"] });
      runSearch(page);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Save failed");
    },
  });

  const enrichMutation = useMutation({
    mutationFn: (input: { people?: DirectoryPerson[]; leadIds?: string[] }) =>
      enrichPeople({
        people: input.people,
        leadIds: input.leadIds,
        revealEmail: true,
        revealPhone: true,
      }),
    onSuccess: (res) => {
      const byApollo = new Map(
        (res.people ?? []).map((p) => [p.apolloPersonId, p]),
      );
      setPeople((prev) =>
        prev.map((p) => byApollo.get(p.apolloPersonId) ?? p),
      );
      if (drawer) {
        setDrawer(byApollo.get(drawer.apolloPersonId) ?? drawer);
      }
      if (leadDrawer && res.leads?.length) {
        const updated = res.leads.find((c) => c.id === leadDrawer.id);
        if (updated) setLeadDrawer(updated);
      }
      setMessage("Enrichment complete");
      setError(null);
      setEnrichingId(null);
      runSearch(page);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Enrich failed");
      setEnrichingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await deleteLead(id);
      }
      return ids.length;
    },
    onSuccess: (n) => {
      setMessage(
        `Moved ${n} Lead${n === 1 ? "" : "s"} to the recycling bin`,
      );
      setError(null);
      setLeadDrawer(null);
      setSelectedIds(new Set());
      runSearch(page);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Delete failed");
    },
  });

  const deleteAllMatchingMutation = useMutation({
    mutationFn: () =>
      bulkDeleteLeads({
        allMatching: true,
        filters: {
          ...filtersToApi(mergeTopSearch(filters, topSearch), 1, perPage),
          scope: "lead",
        },
      }),
    onSuccess: (res) => {
      setMessage(
        `Moved ${res.deleted} matching Lead${res.deleted === 1 ? "" : "s"} to the recycling bin`,
      );
      setError(null);
      setLeadDrawer(null);
      setSelectedIds(new Set());
      runSearch(1);
    },
    onError: (err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Delete all matching failed",
      );
    },
  });

  const createListMutation = useMutation({
    mutationFn: (name: string) =>
      createWorkspaceList({ name, modality: "people" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace-lists"] });
      setNewListName("");
    },
  });

  const saveSearchMutation = useMutation({
    mutationFn: (name: string) =>
      createSavedSearch({
        name,
        filters: filtersToApi(mergeTopSearch(filters, topSearch), page, perPage),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved-person-searches"] });
      setSaveSearchOpen(false);
      setSaveSearchName("");
      setMessage("Search saved");
    },
  });

  const savedOnPage = useMemo(
    () => people.filter((p) => p.alreadySaved).length,
    [people],
  );
  const netNewOnPage = people.length - savedOnPage;

  const visiblePeople = useMemo(() => {
    if (!isLead && mode === "net-new") {
      return people.filter((p) => !p.alreadySaved);
    }
    return people;
  }, [people, mode, isLead]);

  async function openPerson(p: DirectoryPerson) {
    const id = isLead
      ? p.leadId
      : p.personId || p.apolloPersonId;
    if (!id) {
      setError(
        isLead
          ? "Lead is missing a workspace lead id"
          : "Person is missing an id",
      );
      return;
    }
    navigate(isLead ? `/user/leads/${id}` : `/people/${id}`);
  }

  const selectedPeople = useMemo(
    () => people.filter((p) => selectedIds.has(personRowId(p))),
    [people, selectedIds],
  );

  function syncUrl(nextFilters: PeopleFilterState, q: string) {
    const params = new URLSearchParams();
    const trimmed = q.trim();
    if (trimmed) params.set("q", trimmed);
    const encoded = encodeFiltersParam(nextFilters);
    if (encoded !== encodeFiltersParam(EMPTY_PEOPLE_FILTERS)) {
      params.set("filters", encoded);
    }
    lastUrlQ.current = trimmed;
    setSearchParams(params, { replace: true });
  }

  function runSearch(
    nextPage = page,
    nextFilters = filters,
    nextTop = topSearch,
    nextPerPage = perPage,
  ) {
    setPage(nextPage);
    setSearching(true);
    syncUrl(nextFilters, nextTop);
    const apiFilters = {
      ...filtersToApi(
        mergeTopSearch(nextFilters, nextTop),
        nextPage,
        nextPerPage,
      ),
      scope,
    };
    searchPeople(apiFilters)
      .then((res) => {
        setPeople(res.people);
        setTotalEntries(res.totalEntries);
        setSelectedIds(new Set());
        setError(null);
        setMessage(`Found ${res.totalEntries.toLocaleString()} people`);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Search failed");
      })
      .finally(() => setSearching(false));
  }

  /** Reset sidebar filters + page search bar and re-query. */
  function clearFiltersAndSearch() {
    setFilters(EMPTY_PEOPLE_FILTERS);
    setTopSearch("");
    runSearch(1, EMPTY_PEOPLE_FILTERS, "");
  }

  /** Chip remove that should immediately re-search. */
  function applyFilterChange(next: PeopleFilterState) {
    setFilters(next);
    runSearch(1, next, topSearch);
  }

  function changePerPage(n: number) {
    setPerPage(n);
    runSearch(1, filters, topSearch, n);
  }

  useEffect(() => {
    if (autoRan.current) return;
    autoRan.current = true;
    const q = searchParams.get("q") ?? "";
    lastUrlQ.current = q.trim();
    runSearch(1, filters, q || topSearch);
    // Load directory from DynamoDB on first visit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Top-bar (or external) ?q= changes while staying on this page.
  useEffect(() => {
    if (!autoRan.current) return;
    const q = (searchParams.get("q") ?? "").trim();
    if (lastUrlQ.current === q) return;
    lastUrlQ.current = q;
    setTopSearch(q);
    runSearch(1, filters, q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  function applySavedSearch(search: SavedPersonSearch) {
    const next = apiFiltersToState(search.filters);
    setFilters(next);
    setTopSearch("");
    setPage(1);
    setMessage(`Loaded “${search.name}”`);
    runSearch(1, next, "");
  }

  function seedFromIcp() {
    const active =
      icps.find((i) => i.id === icpData?.activeIcpId) ?? icps[0];
    if (!active) {
      setMessage("No ICP found. Create one under Settings → Personas.");
      return;
    }
    const next = seedFiltersFromIcp(active, filters);
    setFilters(next);
    setMessage(`Seeded filters from ICP “${active.name}”`);
    runSearch(1, next, topSearch);
  }

  async function onExport() {
    const rows = selectedPeople.length ? selectedPeople : visiblePeople;
    if (rows.length === 0) return;
    try {
      if (isLead) {
        const leadIds = rows
          .map((p) => p.leadId)
          .filter((s): s is string => Boolean(s));
        const blob = await exportLeadsCsv(leadIds.length ? leadIds : undefined);
        downloadBlob(blob, "leads-export.csv");
      } else {
        const blob = await exportPeopleCsv(rows);
        downloadBlob(blob, "people-export.csv");
      }
      setMessage(`Exported ${rows.length} rows`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    }
  }

  const [workflowPickerOpen, setWorkflowPickerOpen] = useState(false);

  function createWorkflow() {
    if (selectedPeople.length === 0) {
      navigate("/workflows");
      return;
    }
    setWorkflowPickerOpen(true);
  }

  async function saveResearchAsDeals(text: string) {
    const leads = parseLeadEnvelopes(text);
    if (!leads.length) {
      setMessage("No structured leads found to save as deals.");
      return;
    }
    try {
      const offers = leads.map((l, i) => {
        const r = leadToExploreResult(l, i);
        return {
          url: r.url,
          company: r.company,
          role: r.title,
          dealName: r.title,
          location: r.location,
          compensation: r.compensation,
          source: r.source,
        };
      });
      const res = await addDiscoveredOffers(offers, "people-research-ai");
      setMessage(`Saved ${res.added.length} deals (skipped ${res.skipped})`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save deals failed");
    }
  }

  const icpContext = useMemo(() => {
    const active =
      icps.find((i) => i.id === filters.personaIcpId) ??
      icps.find((i) => i.id === icpData?.activeIcpId) ??
      icps[0];
    if (!active) return undefined;
    return [
      `Name: ${active.name}`,
      active.industries?.length
        ? `Industries: ${active.industries.join(", ")}`
        : "",
      active.geographies?.length
        ? `Geographies: ${active.geographies.join(", ")}`
        : "",
      active.icpKeywords?.length
        ? `Keywords: ${active.icpKeywords.join(", ")}`
        : "",
    ]
      .filter(Boolean)
      .join("\n");
  }, [icps, filters.personaIcpId, icpData?.activeIcpId]);

  const totalPages = Math.max(1, Math.ceil(totalEntries / perPage));
  const activeFilterCount = buildFilterChips(filters).length;

  return (
    <div className="flex h-full min-h-0 gap-3 overflow-hidden">
      <div className="hidden h-full md:block">
        <PeopleFilters
          value={filters}
          onChange={setFilters}
          onSearch={() => runSearch(1)}
          onClear={clearFiltersAndSearch}
          searching={searching}
          collapsed={filtersCollapsed}
          onToggleCollapsed={() => setFiltersCollapsed((c) => !c)}
          workspaceScope={isLead}
          icps={icps}
          activeIcpId={icpData?.activeIcpId}
        />
      </div>

      <MobileSheet
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        title="Filters"
        variant="full"
      >
        <PeopleFilters
          embedded
          value={filters}
          onChange={setFilters}
          onSearch={() => {
            setMobileFiltersOpen(false);
            runSearch(1);
          }}
          onClear={() => {
            clearFiltersAndSearch();
            setMobileFiltersOpen(false);
          }}
          searching={searching}
          icps={icps}
          activeIcpId={icpData?.activeIcpId}
          workspaceScope={isLead}
        />
      </MobileSheet>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card">
        <div className="sticky top-0 z-20 shrink-0 border-b border-border bg-card">
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 sm:gap-3 sm:px-5 sm:py-4">
            <h1 className="text-lg font-bold text-foreground">
              {isLead ? "Leads" : "Find people"}
            </h1>
            <form
              className="relative min-w-0 flex-1 basis-full sm:basis-auto sm:min-w-[160px]"
              onSubmit={(e) => {
                e.preventDefault();
                runSearch(1);
              }}
            >
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                value={topSearch}
                onChange={(e) => setTopSearch(e.target.value)}
                placeholder="Search people by name, title, or keyword…"
                className="w-full rounded-md bg-surface-hover py-2 pl-8 pr-3 text-sm outline-none focus:ring-1 focus:ring-border"
              />
            </form>
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(true)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md bg-surface-hover px-2.5 py-1.5 text-xs font-medium text-foreground md:hidden",
                activeFilterCount > 0 && "ring-1 ring-brand/40",
              )}
            >
              <Filter className="size-3.5" />
              Filters
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-brand px-1.5 py-0.5 text-[10px] text-brand-foreground">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {isLead ? (
              <span className="rounded-md bg-surface-hover px-2.5 py-1 text-xs font-medium text-muted-foreground">
                {totalEntries.toLocaleString()} leads
              </span>
            ) : (
              <div className="flex items-center gap-1 rounded-md bg-surface-hover p-0.5">
                <button
                  type="button"
                  onClick={() => setMode("total")}
                  className={cn(
                    "rounded px-2.5 py-1 text-xs font-medium",
                    mode === "total"
                      ? "bg-brand text-brand-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Total ({totalEntries.toLocaleString()})
                </button>
                <button
                  type="button"
                  onClick={() => setMode("net-new")}
                  className={cn(
                    "rounded px-2.5 py-1 text-xs font-medium",
                    mode === "net-new"
                      ? "bg-brand text-brand-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Net new ({netNewOnPage})
                </button>
              </div>
            )}
            <div className="relative">
              <button
                type="button"
                onClick={() => setActionsMenuOpen((o) => !o)}
                className="rounded-md bg-surface-hover px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-surface-hover/80"
              >
                Actions
              </button>
              {actionsMenuOpen && (
                <div className="absolute right-0 z-30 mt-1 w-52 rounded-md bg-popover py-1 shadow-xl ring-1 ring-black/5 dark:ring-white/10">
                  <Link
                    to={isLead ? "/people" : "/user/leads"}
                    className="block px-3 py-1.5 text-xs hover:bg-surface-hover"
                    onClick={() => setActionsMenuOpen(false)}
                  >
                    {isLead ? "Find in directory" : "Leads"}
                  </Link>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover"
                    onClick={() => {
                      setActionsMenuOpen(false);
                      setSaveSearchOpen(true);
                    }}
                  >
                    Save search
                  </button>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover disabled:opacity-40"
                    disabled={visiblePeople.length === 0}
                    onClick={() => {
                      setActionsMenuOpen(false);
                      onExport();
                    }}
                  >
                    Export
                  </button>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover"
                    onClick={() => {
                      setActionsMenuOpen(false);
                      setListPicker(true);
                    }}
                  >
                    {isLead ? "Add selection to list" : "Save selection to list"}
                  </button>
                  <Link
                    to="/lists"
                    className="block px-3 py-1.5 text-xs hover:bg-surface-hover"
                    onClick={() => setActionsMenuOpen(false)}
                  >
                    Manage lists
                  </Link>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover"
                    onClick={() => {
                      setActionsMenuOpen(false);
                      setResearchOpen(true);
                    }}
                  >
                    Research with AI
                  </button>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover disabled:opacity-40"
                    disabled={selectedIds.size === 0}
                    onClick={() => {
                      setActionsMenuOpen(false);
                      createWorkflow();
                    }}
                  >
                    Workflows
                  </button>
                  {isLead && (
                    <button
                      type="button"
                      className="block w-full px-3 py-1.5 text-left text-xs text-destructive hover:bg-surface-hover disabled:opacity-40"
                      disabled={
                        totalEntries === 0 || deleteAllMatchingMutation.isPending
                      }
                      onClick={() => {
                        setActionsMenuOpen(false);
                        if (
                          !window.confirm(
                            `Move all ${totalEntries} matching Lead${totalEntries === 1 ? "" : "s"} to the recycling bin? You can restore them from Settings.`,
                          )
                        ) {
                          return;
                        }
                        deleteAllMatchingMutation.mutate();
                      }}
                    >
                      Delete all matching
                    </button>
                  )}
                  {isLead && (
                    <Link
                      to="/settings/recycling-bin/leads"
                      className="block px-3 py-1.5 text-xs hover:bg-surface-hover"
                      onClick={() => setActionsMenuOpen(false)}
                    >
                      Recycling bin
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2.5 px-5 pb-4">
            <PeopleFilterChips
              value={filters}
              onChange={applyFilterChange}
              onClear={clearFiltersAndSearch}
              personaLabel={
                icps.find((i) => i.id === filters.personaIcpId)?.name
              }
            />
            {savedSearches.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="text-muted-foreground">Saved searches:</span>
                {savedSearches.map((s) => (
                  <button
                    key={s.searchId}
                    type="button"
                    onClick={() => applySavedSearch(s)}
                    className="rounded bg-surface-hover px-2 py-1 hover:text-foreground"
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {showBanner && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-muted/15 px-5 py-2.5 text-xs">
              {isLead ? (
                <>
                  <p className="text-muted-foreground">
                    These are your editable copies of directory people. Changes stay
                    on your records — the directory is unchanged.
                  </p>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/people"
                      className="rounded-md bg-brand px-2.5 py-1 font-medium text-brand-foreground"
                    >
                      Find people
                    </Link>
                    <button
                      type="button"
                      onClick={() => setShowBanner(false)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Dismiss
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Better results start with ICP keywords and industries. Apply a
                    Persona in the filter rail, or connect Apollo under Integrations.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={seedFromIcp}
                      className="rounded-md bg-brand px-2.5 py-1 font-medium text-brand-foreground"
                    >
                      Seed from ICP
                    </button>
                    <Link
                      to="/settings/integrations"
                      className="text-brand hover:underline"
                    >
                      Integrations
                    </Link>
                    <button
                      type="button"
                      onClick={() => setShowBanner(false)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Dismiss
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {(message || error) && (
          <p
            className={cn(
              "shrink-0 px-5 py-2.5 text-xs",
              error
                ? "bg-destructive/10 text-destructive"
                : "text-muted-foreground",
            )}
          >
            {error ?? message}
          </p>
        )}

        <PeopleResultsTable
          people={visiblePeople}
          selectedIds={selectedIds}
          onToggle={(id) =>
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            })
          }
          onToggleAll={(all) =>
            setSelectedIds(
              all
                ? new Set(visiblePeople.map((p) => personRowId(p)))
                : new Set(),
            )
          }
          onOpen={(p) => void openPerson(p)}
          companyDetailBase={isLead ? "/user/accounts" : "/companies"}
        />

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-card px-5 py-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <button
              type="button"
              disabled={page <= 1 || searching}
              onClick={() => runSearch(page - 1)}
              className="rounded-md border border-border px-2 py-1 disabled:opacity-40"
            >
              Prev
            </button>
            <span>
              Page {page} of {totalPages}
              {isLead
                ? ""
                : mode === "net-new"
                  ? ` · ${netNewOnPage} net new on this page`
                  : ` · ${savedOnPage} already in leads on this page`}
            </span>
            <button
              type="button"
              disabled={page >= totalPages || searching}
              onClick={() => runSearch(page + 1)}
              className="rounded-md border border-border px-2 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
          <select
            value={perPage}
            onChange={(e) => changePerPage(Number(e.target.value))}
            className="rounded-md border border-border bg-bg px-2 py-1 text-xs"
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </div>

        {selectedIds.size > 0 && (
          <div className="sticky bottom-0 z-20 flex flex-wrap items-center gap-2 border-t border-border bg-card px-5 py-3.5 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
            <span className="text-sm font-semibold">
              {selectedIds.size} selected
            </span>
            {!isLead && (
              <button
                type="button"
                disabled={saveMutation.isPending}
                onClick={() => saveMutation.mutate({ people: selectedPeople })}
                className="rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
              >
                Save
              </button>
            )}
            {isLead && (
              <button
                type="button"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  const ids = selectedPeople
                    .map((p) => p.leadId)
                    .filter((id): id is string => !!id);
                  if (!ids.length) return;
                  if (
                    !window.confirm(
                      `Move ${ids.length} Lead${ids.length === 1 ? "" : "s"} to the recycling bin? You can restore them from Settings.`,
                    )
                  ) {
                    return;
                  }
                  deleteMutation.mutate(ids);
                }}
                className="rounded-md border border-destructive/40 px-2.5 py-1.5 text-xs text-destructive disabled:opacity-50"
              >
                Delete
              </button>
            )}
            {isLead && (
              <button
                type="button"
                onClick={() => setListPicker(true)}
                className="rounded-md border border-border px-2.5 py-1.5 text-xs"
              >
                Add to list
              </button>
            )}
            {!isLead && (
              <button
                type="button"
                onClick={() => setListPicker(true)}
                className="rounded-md border border-border px-2.5 py-1.5 text-xs"
              >
                Save to list
              </button>
            )}
            {isLead && (
              <button
                type="button"
                disabled={saveMutation.isPending}
                onClick={() =>
                  saveMutation.mutate({
                    people: selectedPeople,
                    createDeal: true,
                  })
                }
                className="rounded-md border border-border px-2.5 py-1.5 text-xs"
              >
                Create deals
              </button>
            )}
            {isLead && (
              <button
                type="button"
                onClick={createWorkflow}
                className="rounded-md border border-border px-2.5 py-1.5 text-xs"
              >
                Workflows
              </button>
            )}
            <button
              type="button"
              onClick={onExport}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="ml-auto text-xs text-muted-foreground hover:text-foreground"
            >
              Clear selection
            </button>
          </div>
        )}
      </div>

      {drawer && (
        <PersonDrawer
          person={drawer}
          onClose={() => setDrawer(null)}
          saving={saveMutation.isPending}
          onSave={() => saveMutation.mutate({ people: [drawer] })}
        />
      )}

      {leadDrawer && (
        <SavedPersonDrawer
          person={leadDrawer}
          onClose={() => setLeadDrawer(null)}
          enriching={
            enrichMutation.isPending && enrichingId === leadDrawer.id
          }
          deleting={deleteMutation.isPending}
          onEnrich={() => {
            setEnrichingId(leadDrawer.id);
            enrichMutation.mutate({ leadIds: [leadDrawer.id] });
          }}
          onDelete={() => deleteMutation.mutate([leadDrawer.id])}
        />
      )}

      {listPicker && (
        <ListPickerModal
          lists={lists}
          newListName={newListName}
          onNewListName={setNewListName}
          creating={createListMutation.isPending}
          onCreate={() => {
            if (newListName.trim())
              createListMutation.mutate(newListName.trim());
          }}
          onPick={(listId) => {
            saveMutation.mutate({ people: selectedPeople, listId });
            setListPicker(false);
          }}
          onClose={() => setListPicker(false)}
        />
      )}

      {saveSearchOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
          onClick={() => setSaveSearchOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg border border-border bg-card p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold">Save search</h3>
            <input
              value={saveSearchName}
              onChange={(e) => setSaveSearchName(e.target.value)}
              placeholder="Search name"
              className="mt-3 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSaveSearchOpen(false)}
                className="text-xs text-muted-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!saveSearchName.trim() || saveSearchMutation.isPending}
                onClick={() => saveSearchMutation.mutate(saveSearchName.trim())}
                className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {workflowPickerOpen && (
        <WorkflowEnrollPicker
          apolloPersonIds={selectedPeople
            .map((p) => p.apolloPersonId)
            .filter((id): id is string => Boolean(id))}
          leadIds={selectedPeople
            .map((p) => p.leadId)
            .filter((id): id is string => Boolean(id))}
          onClose={() => setWorkflowPickerOpen(false)}
        />
      )}

      <PeopleResearchAiModal
        open={researchOpen}
        onClose={() => setResearchOpen(false)}
        icpContext={icpContext}
        onApplyKeywords={(kw) => {
          setFilters((f) => ({
            ...f,
            qKeywords: f.qKeywords.trim()
              ? `${f.qKeywords.trim()} ${kw}`
              : kw,
          }));
          setMessage("Keywords applied from AI research");
        }}
        onSaveAsDeals={(text) => {
          void saveResearchAsDeals(text);
        }}
      />
    </div>
  );
}

function mergeTopSearch(
  filters: PeopleFilterState,
  topSearch: string,
): PeopleFilterState {
  const q = topSearch.trim();
  if (!q) return filters;
  const existing = filters.qKeywords.trim();
  if (existing.includes(q)) return filters;
  return {
    ...filters,
    qKeywords: existing ? `${existing} ${q}` : q,
  };
}

function ListPickerModal({
  lists,
  newListName,
  onNewListName,
  onCreate,
  creating,
  onPick,
  onClose,
}: {
  lists: WorkspaceList[];
  newListName: string;
  onNewListName: (v: string) => void;
  onCreate: () => void;
  creating?: boolean;
  onPick: (listId: string) => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-lg border border-border bg-card p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold">Save to list</h3>
        <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto">
          {lists.length === 0 && (
            <li className="text-xs text-muted-foreground">No lists yet.</li>
          )}
          {lists.map((l) => (
            <li key={l.listId}>
              <button
                type="button"
                onClick={() => onPick(l.listId)}
                className="w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/20"
              >
                {l.name}{" "}
                <span className="text-xs text-muted-foreground">
                  ({l.memberCount})
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex gap-2">
          <input
            value={newListName}
            onChange={(e) => onNewListName(e.target.value)}
            placeholder="New list name"
            className="flex-1 rounded-md border border-border bg-bg px-2 py-1.5 text-sm"
          />
          <button
            type="button"
            disabled={!newListName.trim() || creating}
            onClick={onCreate}
            className="rounded-md border border-border px-2 py-1.5 text-xs disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}
